import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button, Card, Badge, Modal, FormField, Empty, Spinner } from '../components/ui';
import PropertyWizard from '../components/PropertyWizard';

const CERT_LABELS = { epc:'EPC', gas_safety:'Gas Safety Certificate', eicr:'EICR', smoke_alarm:'Smoke Alarm Check', co_alarm:'CO Alarm Check', legionella:'Legionella Risk Assessment' };
const CERT_TYPES  = ['epc','gas_safety','eicr','smoke_alarm','co_alarm','legionella'];
const DOC_TYPES   = ['passport','brp','share_code','uk_passport'];

const CERT_VALIDITY_MONTHS = { epc:120, gas_safety:12, eicr:60, smoke_alarm:12, co_alarm:12, legionella:24 };

// Expiry = issue date + validity period
function addMonthsTo(dateStr, months) {
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
}

function getRequiredCerts(property) {
  const base = ['epc', 'eicr', 'smoke_alarm', 'co_alarm', 'legionella'];
  return property.has_gas !== false ? ['gas_safety', ...base] : base;
}

function getComplianceIssues(property, certs) {
  if (!property.setup_complete) return [];
  const required  = getRequiredCerts(property);
  const certMap   = Object.fromEntries((certs || []).map(c => [c.cert_type, c]));
  const wa        = property.wizard_answers || {};
  const statuses  = wa.certStatuses || {};
  const issues    = [];

  required.forEach(key => {
    const cert   = certMap[key];
    const label  = CERT_LABELS[key] || key;
    const status = statuses[key];
    if (!cert && status === 'missing')       issues.push({ level:'high',   key, label, reason:'Missing — not yet obtained' });
    else if (!cert && !status)               issues.push({ level:'medium', key, label, reason:'Not recorded' });
    else if (cert?.status === 'expired')     issues.push({ level:'high',   key, label, reason:'Expired', certId: cert.id });
    else if (cert?.status === 'due_soon')    issues.push({ level:'medium', key, label, reason:'Expiring soon', certId: cert.id });
  });
  return issues;
}

export default function Properties() {
  const [properties, setProperties]           = useState([]);
  const [selected, setSelected]               = useState(null);
  const [certs, setCerts]                     = useState([]);
  const [tenants, setTenants]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showWizard, setShowWizard]           = useState(false);
  const [wizardPropertyId, setWizardPropertyId] = useState(null);
  const [showCertModal, setShowCertModal]     = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showRentModal, setShowRentModal]     = useState(false);
  const [rentForm, setRentForm]               = useState({ monthly_amount: '', due_day: '1', payment_method: 'bank_transfer' });
  const [quickAddKey, setQuickAddKey]         = useState(null); // pre-fill cert type
  const [saving, setSaving]                   = useState(false);
  const [uploadFile, setUploadFile]           = useState(null);
  const [uploading, setUploading]             = useState(false);
  const [certError, setCertError]             = useState('');
  const [completionMap, setCompletionMap]     = useState({});
  const [issuesMap, setIssuesMap]             = useState({});

  const emptyCert   = { cert_type:'gas_safety', issued_date:'', expiry_date:'', notes:'' };
  const emptyTenant = { full_name:'', email:'', nationality:'British', doc_type:'uk_passport', check_date:new Date().toISOString().split('T')[0], doc_expiry_date:'', tenancy_start:'', tenancy_end:'' };
  const [certForm,   setCertForm]   = useState(emptyCert);
  const [tenantForm, setTenantForm] = useState(emptyTenant);

  useEffect(() => { loadProperties(); }, []);

  async function loadProperties() {
    const props = await api.getProperties();
    setProperties(props || []);
    setLoading(false);
    const cMap = {}, iMap = {};
    await Promise.all((props || []).map(async p => {
      const c = await api.getCertificates(p.id);
      cMap[p.id] = c || [];
      iMap[p.id] = getComplianceIssues(p, c || []);
    }));
    setCompletionMap(cMap);
    setIssuesMap(iMap);
  }

  async function selectProperty(p) {
    setSelected(p);
    const [c, t] = await Promise.all([api.getCertificates(p.id), api.getTenants(p.id)]);
    setCerts(c || []); setTenants(t || []);
  }

  async function handleWizardComplete(propId) {
    setShowWizard(false); setWizardPropertyId(null);
    await loadProperties();
    const props = await api.getProperties();
    const p = (props || []).find(x => x.id === propId);
    if (p) selectProperty(p);
  }

  function openNewWizard()          { setWizardPropertyId(null); setShowWizard(true); }
  function resumeWizard(e, propId)  { e.stopPropagation(); setWizardPropertyId(propId); setShowWizard(true); }

  // Quick-add cert: pre-fill type and auto-fill expiry date
  function openQuickAdd(key) {
    setCertForm({ cert_type: key, issued_date: '', expiry_date: '', notes: '' });
    setQuickAddKey(key);
    setShowCertModal(true);
  }

  async function deleteProp(id) {
    if (!window.confirm('Delete this property and all its data?')) return;
    await api.deleteProperty(id);
    setProperties(ps => ps.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
  }


  function openRentModal() {
    setRentForm({
      monthly_amount: selected.rent_amount_gbp ? (selected.rent_amount_gbp / 100).toString() : '',
      due_day: selected.rent_due_day?.toString() || '1',
      payment_method: selected.rent_payment_method || 'bank_transfer',
    });
    setShowRentModal(true);
  }

  async function saveRent() {
    const token = localStorage.getItem('token');
    const ac = localStorage.getItem('lc_access_code');
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(ac ? { 'X-Access-Code': ac } : {}) };
    const body = {
      rent_amount_gbp: rentForm.monthly_amount ? Math.round(parseFloat(rentForm.monthly_amount) * 100) : 0,
      rent_due_day: parseInt(rentForm.due_day),
      rent_payment_method: rentForm.payment_method,
    };
    await fetch(`/api/properties/${selected.id}/rent`, { method: 'PUT', headers, body: JSON.stringify(body) });
    setSelected(p => ({ ...p, ...body }));
    setShowRentModal(false);
  }

  async function saveCert() {
    if (!selected?.id) return;
    setCertError('');
    setSaving(true);
    try {
      let docUrl = certForm.doc_url || '';

      if (uploadFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          docUrl = data.url;
        }
        setUploading(false);
      }

      // Use returned cert directly — avoids re-fetch display issues
      const saved = await api.createCertificate(selected.id, { ...certForm, doc_url: docUrl });
      const newCerts = [...certs, saved];
      setCerts(newCerts);
      setIssuesMap(m => ({ ...m, [selected.id]: getComplianceIssues(selected, newCerts) }));
      setShowCertModal(false);
      setCertForm(emptyCert);
      setUploadFile(null);
      setQuickAddKey(null);
    } catch (err) {
      setCertError(err.message || 'Failed to save certificate');
    } finally { setSaving(false); setUploading(false); }
  }

  async function deleteCert(id) {
    await api.deleteCertificate(id);
    const updated = await api.getCertificates(selected.id);
    setCerts(updated || []);
    setIssuesMap(m => ({ ...m, [selected.id]: getComplianceIssues(selected, updated || []) }));
  }

  async function saveTenant() {
    setSaving(true);
    try {
      const created = await api.createTenant(selected.id, tenantForm);
      setTenants(ts => [...ts, created]);
      setShowTenantModal(false); setTenantForm(emptyTenant);
    } finally { setSaving(false); }
  }

  async function deleteTenant(id) {
    await api.deleteTenant(id);
    setTenants(ts => ts.filter(t => t.id !== id));
  }

  if (loading) return <Spinner />;
  if (showWizard) return (
    <PropertyWizard
      existingPropertyId={wizardPropertyId}
      onComplete={handleWizardComplete}
      onCancel={() => { setShowWizard(false); setWizardPropertyId(null); }}
    />
  );

  const issues = selected ? (issuesMap[selected.id] || []) : [];
  const highIssues   = issues.filter(i => i.level === 'high');
  const mediumIssues = issues.filter(i => i.level === 'medium');

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:32 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Properties</h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>{properties.length} propert{properties.length !== 1 ? 'ies' : 'y'} registered</p>
        </div>
        <Button onClick={openNewWizard}>+ Add Property</Button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: selected ? '300px 1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap:24 }}>

        {/* Property list */}
        <div>
          {!properties.length ? (
            <Empty icon="🏘" title="No properties yet" subtitle="Use the wizard to add your first property" action={<Button onClick={openNewWizard}>Start setup wizard</Button>} />
          ) : (
            properties.map(p => {
              const propIssues   = issuesMap[p.id] || [];
              const isIncomplete = !p.setup_complete;
              const highCount    = propIssues.filter(i => i.level === 'high').length;
              const medCount     = propIssues.filter(i => i.level === 'medium').length;
              const borderColor  = selected?.id === p.id ? 'var(--accent)' : isIncomplete ? 'var(--border)' : highCount > 0 ? 'var(--danger)' : medCount > 0 ? 'var(--warn)' : 'var(--border)';
              return (
                <Card key={p.id} onClick={() => selectProperty(p)} style={{ marginBottom:12, cursor:'pointer', borderColor, transition:'all .15s' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div style={{ flex:1 }}>
                      <p style={{ fontWeight:500, marginBottom:2 }}>{p.address_line1}</p>
                      <p style={{ fontSize:13, color:'var(--ink-3)' }}>{p.city}, {p.postcode}</p>
                      <p style={{ fontSize:12, color:'var(--ink-3)', marginTop:4, textTransform:'capitalize' }}>{p.property_type?.replace('_',' ')} · {p.bedrooms} bed</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); deleteProp(p.id); }} style={{ background:'none', border:'none', fontSize:14, cursor:'pointer', color:'var(--danger)' }}>🗑</button>
                  </div>
                  <div style={{ marginTop:10 }}>
                    {isIncomplete ? (
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:11, color:'var(--ink-3)' }}>⏳ Setup in progress</span>
                        <button onClick={e => resumeWizard(e, p.id)} style={{ fontSize:12, color:'var(--accent)', fontWeight:600, background:'none', border:'none', cursor:'pointer', padding:0 }}>Continue →</button>
                      </div>
                    ) : highCount > 0 || medCount > 0 ? (
                      <div style={{ display:'flex', gap:8 }}>
                        {highCount > 0 && <span style={{ fontSize:11, color:'var(--danger)', fontWeight:600 }}>🔴 {highCount} issue{highCount > 1 ? 's' : ''}</span>}
                        {medCount  > 0 && <span style={{ fontSize:11, color:'var(--warn)',   fontWeight:600 }}>🟡 {medCount} warning{medCount > 1 ? 's' : ''}</span>}
                      </div>
                    ) : (
                      <span style={{ fontSize:11, color:'var(--accent)', fontWeight:600 }}>✅ Compliant</span>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Property detail */}
        {selected && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:20 }}>
              <div>
                <h2 style={{ fontSize:22, marginBottom:4 }}>{selected.address_line1}</h2>
                <p style={{ color:'var(--ink-3)', fontSize:14 }}>{selected.city}, {selected.postcode} · {selected.property_type?.replace('_',' ')} · {selected.bedrooms} bed</p>
              </div>
              {!selected.setup_complete && (
                <Button size="sm" variant="ghost" onClick={() => resumeWizard({ stopPropagation:()=>{} }, selected.id)}>
                  Continue wizard →
                </Button>
              )}
            </div>

            {/* Compliance issues panel */}
            {issues.length > 0 && (
              <Card style={{ marginBottom:20, borderColor: highIssues.length > 0 ? 'var(--danger)' : 'var(--warn)', background: highIssues.length > 0 ? 'var(--danger-lt)' : 'var(--warn-lt)' }}>
                <h3 style={{ fontSize:16, marginBottom:14, color: highIssues.length > 0 ? 'var(--danger)' : 'var(--warn)' }}>
                  {highIssues.length > 0 ? '🚨' : '⚠️'} {issues.length} compliance issue{issues.length > 1 ? 's' : ''} found
                </h3>
                {[...highIssues, ...mediumIssues].map((issue, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'rgba(255,255,255,.7)', borderRadius:8, padding:'10px 14px', marginBottom:8 }}>
                    <div>
                      <span style={{ fontSize:13, fontWeight:600, color: issue.level === 'high' ? 'var(--danger)' : 'var(--warn)' }}>
                        {issue.level === 'high' ? '🔴' : '🟡'} {issue.label}
                      </span>
                      <span style={{ fontSize:12, color:'var(--ink-3)', marginLeft:8 }}>· {issue.reason}</span>
                    </div>
                    {/* Quick-add button for missing/expired certs */}
                    {!issue.certId && (
                      <Button size="sm" onClick={() => openQuickAdd(issue.key)}>
                        + Add cert
                      </Button>
                    )}
                  </div>
                ))}
              </Card>
            )}

            {/* All compliant */}
            {selected.setup_complete && issues.length === 0 && (
              <div style={{ background:'var(--accent-lt)', border:'1px solid var(--accent)', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
                <p style={{ color:'var(--accent)', fontWeight:600, fontSize:14 }}>✅ This property is fully compliant</p>
              </div>
            )}

            {/* Certificates table */}
            <Card style={{ marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 }}>
                <h3 style={{ fontSize:17 }}>Certificates & Safety</h3>
                <Button size="sm" onClick={() => { setCertForm(emptyCert); setQuickAddKey(null); setShowCertModal(true); }}>+ Add</Button>
              </div>
              {!certs.length ? (
                <p style={{ color:'var(--ink-3)', fontSize:14 }}>No certificates added yet</p>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:'1.5px solid var(--border)' }}>
                      {['Type','Issued','Expires','Status',''].map(h => <th key={h} style={{ textAlign:'left', padding:'6px 10px', fontSize:12, color:'var(--ink-3)', fontWeight:500 }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {certs.map(c => (
                      <tr key={c.id} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'10px 10px', fontSize:14 }}>{CERT_LABELS[c.cert_type] || c.cert_type}</td>
                        <td style={{ padding:'10px 10px', fontSize:13, fontFamily:'var(--font-mono)' }}>{c.issued_date || '—'}</td>
                        <td style={{ padding:'10px 10px', fontSize:13, fontFamily:'var(--font-mono)' }}>{c.expiry_date || '—'}</td>
                        <td style={{ padding:'10px 10px' }}><Badge status={c.status || 'unknown'} /></td>
                        <td style={{ padding:'10px 10px' }}>
                          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                            {c.doc_url && (
                              <a href={c.doc_url} target="_blank" rel="noreferrer" style={{ fontSize:12, color:'var(--accent)' }}>📎 View</a>
                            )}
                            <button onClick={() => deleteCert(c.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', fontSize:13 }}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            {/* Tenants */}
            <Card>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 }}>
                <h3 style={{ fontSize:17 }}>Tenants & Right to Rent</h3>
                <Button size="sm" onClick={() => setShowTenantModal(true)}>+ Add</Button>
              </div>
              {!tenants.length ? (
                <p style={{ color:'var(--ink-3)', fontSize:14 }}>No tenants added yet</p>
              ) : tenants.map(t => (
                <div key={t.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <p style={{ fontWeight:500, fontSize:14 }}>{t.full_name}</p>
                    <p style={{ fontSize:12, color:'var(--ink-3)' }}>{t.nationality} · {t.doc_type?.replace('_',' ')} · Checked: {t.check_date}</p>
                  </div>
                  <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                    {t.doc_expiry_date && <Badge status={new Date(t.doc_expiry_date) < new Date() ? 'expired' : 'valid'} label={t.doc_expiry_date} />}
                    <button onClick={() => deleteTenant(t.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', fontSize:13 }}>Remove</button>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        )}
      </div>

      {/* Add/Quick-add Certificate Modal */}
      <Modal open={showCertModal} onClose={() => { setShowCertModal(false); setQuickAddKey(null); setUploadFile(null); setCertError(''); }} title={quickAddKey ? `Add ${CERT_LABELS[quickAddKey]}` : 'Add Certificate'}>
        <FormField label="Certificate type">
          <select value={certForm.cert_type} onChange={e => {
            const key    = e.target.value;
              setCertForm(f => ({ ...f, cert_type: key, expiry_date: '', issued_date: '' }));
          }}>
            {CERT_TYPES.map(t => <option key={t} value={t}>{CERT_LABELS[t]}</option>)}
          </select>
        </FormField>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <FormField label="Issue date (optional)">
            <input type="date" value={certForm.issued_date} onChange={e => {
            const issued = e.target.value;
            setCertForm(f => ({
              ...f,
              issued_date: issued,
              expiry_date: issued && CERT_VALIDITY_MONTHS[f.cert_type]
                ? addMonthsTo(issued, CERT_VALIDITY_MONTHS[f.cert_type])
                : f.expiry_date,
            }));
          }} />
          </FormField>
          <FormField label="Expiry date">
            <input type="date" value={certForm.expiry_date} onChange={e => setCertForm(f => ({...f, expiry_date: e.target.value}))} />
            {certForm.expiry_date && certForm.issued_date && <p style={{ fontSize:11, color:'var(--accent)', marginTop:3 }}>Auto-calculated from issue date — adjust if needed</p>}
          </FormField>
        </div>
        <FormField label="Upload certificate (PDF or image — optional)">
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={e => setUploadFile(e.target.files[0] || null)}
            style={{ border:'1.5px solid var(--border)', borderRadius:'var(--radius)', padding:'8px 12px', width:'100%', background:'var(--bg-card)', fontSize:13 }}
          />
          {uploadFile && (
            <div style={{ marginTop:6, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <p style={{ fontSize:12, color:'var(--accent)' }}>📎 {uploadFile.name} ({(uploadFile.size/1024).toFixed(0)}KB)</p>
              <button onClick={() => setUploadFile(null)} style={{ fontSize:11, color:'var(--danger)', background:'none', border:'none', cursor:'pointer' }}>Remove</button>
            </div>
          )}
        </FormField>
        <FormField label="Notes (optional)">
          <textarea rows={2} value={certForm.notes} onChange={e => setCertForm(f => ({...f, notes: e.target.value}))} />
        </FormField>
        <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
          <Button variant="ghost" onClick={() => { setShowCertModal(false); setQuickAddKey(null); setUploadFile(null); }}>Cancel</Button>
          {certError && <p style={{ color:'var(--danger)', fontSize:13, marginTop:8 }}>{certError}</p>}
          <Button loading={saving || uploading} onClick={saveCert}>{uploading ? 'Uploading…' : 'Save certificate'}</Button>
        </div>
      </Modal>

      {/* Rent Settings Section */}
      {selected && (
        <div style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 12, padding: 20, marginTop: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>💷 Rent Settings</h3>
            <Button size="sm" onClick={openRentModal}>Edit</Button>
          </div>
          {selected.rent_amount_gbp ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
              <div>
                <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Monthly rent</p>
                <p style={{ fontWeight: 600 }}>£{(selected.rent_amount_gbp / 100).toLocaleString()}</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Due day</p>
                <p style={{ fontWeight: 600 }}>{selected.rent_due_day}{selected.rent_due_day === 1 ? 'st' : selected.rent_due_day === 2 ? 'nd' : selected.rent_due_day === 3 ? 'rd' : 'th'} of month</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>Payment method</p>
                <p style={{ fontWeight: 600, textTransform: 'capitalize' }}>{(selected.rent_payment_method || 'bank_transfer').replace('_', ' ')}</p>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>No rent settings configured. <button onClick={openRentModal} style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: 0 }}>Set up now →</button></p>
          )}
        </div>
      )}

      {/* Rent Settings Modal */}
      <Modal open={showRentModal} onClose={() => setShowRentModal(false)} title="Rent Settings">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Monthly rent amount (£)</label>
            <input type="number" min="0" step="1" value={rentForm.monthly_amount}
              onChange={e => setRentForm(f => ({ ...f, monthly_amount: e.target.value }))}
              placeholder="1200"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Rent due day of month</label>
            <select value={rentForm.due_day} onChange={e => setRentForm(f => ({ ...f, due_day: e.target.value }))}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of each month</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Payment method</label>
            <select value={rentForm.payment_method} onChange={e => setRentForm(f => ({ ...f, payment_method: e.target.value }))}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 15, background: 'var(--bg)', boxSizing: 'border-box' }}>
              <option value="bank_transfer">Bank transfer</option>
              <option value="standing_order">Standing order</option>
              <option value="direct_debit">Direct debit</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowRentModal(false)}>Cancel</Button>
            <Button onClick={saveRent}>Save rent settings</Button>
          </div>
        </div>
      </Modal>

      {/* Add Tenant Modal */}
      <Modal open={showTenantModal} onClose={() => setShowTenantModal(false)} title="Add Tenant">
        <FormField label="Full name"><input value={tenantForm.full_name} onChange={e => setTenantForm(f => ({...f, full_name: e.target.value}))} /></FormField>
        <FormField label="Email (optional)"><input type="email" value={tenantForm.email} onChange={e => setTenantForm(f => ({...f, email: e.target.value}))} /></FormField>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <FormField label="Nationality"><input value={tenantForm.nationality} onChange={e => setTenantForm(f => ({...f, nationality: e.target.value}))} /></FormField>
          <FormField label="Document type">
            <select value={tenantForm.doc_type} onChange={e => setTenantForm(f => ({...f, doc_type: e.target.value}))}>
              {DOC_TYPES.map(d => <option key={d} value={d}>{d.replace('_',' ')}</option>)}
            </select>
          </FormField>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <FormField label="Check date"><input type="date" value={tenantForm.check_date} onChange={e => setTenantForm(f => ({...f, check_date: e.target.value}))} /></FormField>
          <FormField label="Document expiry"><input type="date" value={tenantForm.doc_expiry_date} onChange={e => setTenantForm(f => ({...f, doc_expiry_date: e.target.value}))} /></FormField>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <FormField label="Tenancy start"><input type="date" value={tenantForm.tenancy_start} onChange={e => setTenantForm(f => ({...f, tenancy_start: e.target.value}))} /></FormField>
          <FormField label="Tenancy end"><input type="date" value={tenantForm.tenancy_end} onChange={e => setTenantForm(f => ({...f, tenancy_end: e.target.value}))} /></FormField>
        </div>
        <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
          <Button variant="ghost" onClick={() => setShowTenantModal(false)}>Cancel</Button>
          <Button loading={saving} onClick={saveTenant}>Add tenant</Button>
        </div>
      </Modal>
    </div>
  );
}